// Self-order receipt print/share helpers.
// - Settings persisted in localStorage ("SelfReceiptSettings"): autoPrint + share channels.
// - PDF generated from the rendered PosReceiptTemplate node (thermal width).
// - Share: WhatsApp auto-send (reuses sendToWs/send-message) when enabled + mobile present,
//   otherwise the native share sheet (navigator.share), with manual links as a final fallback.

import html2pdf from 'html2pdf.js';
import { sendWhatsAppMessageAPI } from '../component/Modules/PointOfSale/Configuration/BranchSettings/BranchSettingsAPI';
import { getPPI } from '../component/Shared/print/printUtils';
import { errorSnackMsg, successSnackMsg } from '../component/Custom Hooks/SnackkBarUtilities';

const SETTINGS_KEY = 'SelfReceiptSettings';

const DEFAULT_SETTINGS = {
  autoPrint: false,
  whatsapp: false,
  sms: false,
  email: false,
};

export const getSelfReceiptSettings = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}');
    return { ...DEFAULT_SETTINGS, ...parsed };
  } catch (_) {
    return { ...DEFAULT_SETTINGS };
  }
};

export const setSelfReceiptSettings = (settings) => {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify({ ...DEFAULT_SETTINGS, ...settings }));
  } catch (_) {
    /* ignore quota errors */
  }
};

// Build a thermal-width PDF blob from a receipt DOM node (matches the configured roll size).
export const generateReceiptPdfBlob = async (node) => {
  if (!node) return null;
  let printSettings = {};
  try {
    printSettings = JSON.parse(localStorage.getItem('PrinterSettings') || '{}');
  } catch (_) {
    printSettings = {};
  }
  const roll = printSettings?.receiptSize === '58' ? 58 : 80;
  const widthMM = roll === 80 ? 72 : 48;

  const ppi = getPPI();
  const pxToMM = (px) => px * (25.4 / ppi);
  const contentHeightMM = Math.max(60, pxToMM(node.scrollHeight || node.offsetHeight || 0) * 1.1);

  const opt = {
    margin: 1,
    html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
    jsPDF: { unit: 'mm', format: [widthMM, contentHeightMM], orientation: 'portrait' },
    pagebreak: { mode: ['css', 'legacy'] },
  };
  return html2pdf().from(node).set(opt).outputPdf('blob');
};

const digitsOnly = (value) => (value || '').toString().replace(/[^0-9]/g, '');

// Share a receipt. Returns an object describing what happened, including manual
// fallback links when neither auto-send nor the native share sheet are available.
export const shareSelfReceipt = async ({
  node,
  fileName = 'receipt.pdf',
  message = 'Your receipt',
  shareUrl = '',
  customerMobile = '',
  customerEmail = '',
  branchId = '',
}) => {
  const settings = getSelfReceiptSettings();
  const mobile = digitsOnly(customerMobile);
  const fullText = shareUrl ? `${message} ${shareUrl}` : message;

  // 1) WhatsApp auto-send when ticked and we have a mobile number.
  if (settings.whatsapp && mobile) {
    try {
      const blob = await generateReceiptPdfBlob(node);
      const formData = new FormData();
      formData.append('userId', branchId || '');
      formData.append('phoneNumber', mobile);
      formData.append('message', fullText);
      if (blob) formData.append('file', new File([blob], fileName, { type: 'application/pdf' }));
      await sendWhatsAppMessageAPI(formData, () => {}, () => {});
      successSnackMsg('Receipt sent via WhatsApp');
      return { method: 'whatsapp' };
    } catch (_) {
      errorSnackMsg('Failed to send via WhatsApp, opening share options');
      // fall through to native share
    }
  }

  // 2) Native share sheet (works on mobile/tablet). Prefer sharing the PDF file.
  try {
    const blob = await generateReceiptPdfBlob(node);
    const file = blob ? new File([blob], fileName, { type: 'application/pdf' }) : null;
    if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: 'Receipt', text: message });
      return { method: 'native-file' };
    }
    if (navigator.share) {
      await navigator.share({ title: 'Receipt', text: message, url: shareUrl || undefined });
      return { method: 'native-text' };
    }
  } catch (err) {
    if (err?.name === 'AbortError') return { method: 'cancelled' };
  }

  // 3) Manual fallback links (desktop / unsupported native share).
  return {
    method: 'manual',
    links: {
      whatsapp: `https://wa.me/${mobile}?text=${encodeURIComponent(fullText)}`,
      sms: `sms:${mobile}${/Android/i.test(navigator.userAgent) ? '?' : '&'}body=${encodeURIComponent(fullText)}`,
      email: `mailto:${customerEmail || ''}?subject=${encodeURIComponent('Your Receipt')}&body=${encodeURIComponent(fullText)}`,
    },
  };
};
